import React, { useState, useEffect } from "react";
import { Modal, Table, Button, Input, Select, message } from "antd";
import { useSelector, useDispatch } from "react-redux";
import { ArrowLeftOutlined, SettingOutlined } from "@ant-design/icons";
import {
  patchFuelInfoByUniqueID,
  fetchFuelInfoByUniqueID,
  fetchRawData,
  postFuelInfoApplyToAll,
} from "../../../features/RawData/RawDataSlice";
import ConflictingDataModal from "./ConflictingDataModal";
import BiofuelCompositionModal from "../../../pages/freemium/fuelModals/BiofuelCompositionModal";

const { Option } = Select;

const FuelEditModal = ({ visible, setIsModalVisible, selectedUniqueId }) => {
  const fuelDataByUniqueId = useSelector((state) => state.rawData.fuelDataByUniqueId);
  const {
    rawData,
    blendedFuelsData,
    selectedRecordsPerPageUniqueIds,
    cardDataTypeIndividual,
    globalFilters,
  } = useSelector((state) => state.rawData);
  const { user } = useSelector((state) => state.user);
  const dispatch = useDispatch();

  const [isTableEdited, setIsTableEdited] = useState(false);
  const [editingData, setEditingData] = useState([]);
  const [userRemark, setUserRemark] = useState("");
  const [remarkError, setRemarkError] = useState(false);
  const [isConflictingDataModalVisible, setConflictingDataModalVisible] = useState(false);
  const [bioFuelModalVisible, setBioFuelModalVisible] = useState(false);
  const [activeBiofuelId, setActiveBiofuelId] = useState(null);

  const selectedCards = Object.values(selectedRecordsPerPageUniqueIds).flat();
  const disabled = user["is_admin:"] ? false : rawData[selectedUniqueId]?.[0]?.is_locked_in;

  useEffect(() => {
    if (fuelDataByUniqueId) setEditingData(fuelDataByUniqueId);
  }, [fuelDataByUniqueId, visible]);

  useEffect(() => {
    if (selectedUniqueId === blendedFuelsData?.general_info?.unique_id) {
      const fuelExists = fuelDataByUniqueId?.some(
        (fuel) =>
          fuel?.id === blendedFuelsData?.general_info?.fuel_consumption_id &&
          fuel?.class_name === "Biofuel"
      );
      if (fuelExists) {
        setEditingData((prev) =>
          prev.map((fuel) =>
            fuel?.id === blendedFuelsData?.general_info?.fuel_consumption_id
              ? {
                  ...fuel,
                  blended_id: blendedFuelsData?.blend_id,
                  fuel_name: blendedFuelsData?.general_info?.fuel_name,
                  fuel_ef_ttw_gfuel: blendedFuelsData?.general_info?.ef_ttw_gfuel,
                  fuel_ef_ttw_MJfuel: blendedFuelsData?.general_info?.ef_ttw_MJfuel,
                  bunker_survey: blendedFuelsData?.general_info?.bunker_survey,
                  bunker_quantity: blendedFuelsData?.general_info?.bunker_quantity,
                  fuel_ef_wtt_gfuel: blendedFuelsData?.general_info?.ef_wtt_gfuel,
                  fuel_ef_wtt_MJfuel: blendedFuelsData?.general_info?.ef_wtt_MJfuel,
                  fuel_lcv: blendedFuelsData?.general_info?.lcv,
                  modified: {
                    fuel_name: blendedFuelsData?.general_info?.fuel_name === fuel?.fuel_name,
                    fuel_ef_ttw_gfuel: blendedFuelsData?.general_info?.ef_ttw_gfuel !== fuel?.fuel_ef_ttw_gfuel,
                    fuel_ef_ttw_MJfuel: blendedFuelsData?.general_info?.ef_ttw_MJfuel !== fuel?.fuel_ef_ttw_MJfuel,
                    bunker_survey: blendedFuelsData?.general_info?.bunker_survey !== fuel?.bunker_survey,
                    bunker_quantity: blendedFuelsData?.general_info?.bunker_quantity !== fuel?.bunker_quantity,
                    fuel_ef_wtt_gfuel: blendedFuelsData?.general_info?.ef_wtt_gfuel !== fuel?.fuel_ef_wtt_gfuel,
                    fuel_ef_wtt_MJfuel: blendedFuelsData?.general_info?.ef_wtt_MJfuel !== fuel?.fuel_ef_wtt_MJfuel,
                    fuel_lcv: blendedFuelsData?.general_info?.lcv !== fuel?.fuel_lcv,
                  },
                }
              : fuel
          )
        );
        setIsTableEdited(true);
      }
    }
  }, [blendedFuelsData]);

  const createPayload = ({ oldData, newData }) => {
    const specifiedFields = [
      "name", "consumption", "cslip", "rwdi", "class_name", "lcv",
      "ef_ttw_MJfuel", "ef_ttw_gfuel", "ef_wtt_MJfuel", "ef_wtt_gfuel",
      "rob", "bunker_survey", "bunker_quantity",
    ];
    let changedFieldsSet = new Set();

    const changes = newData.reduce((acc, newRecord) => {
      const oldRecord = oldData.find((r) => r.id === newRecord.id);
      if (!oldRecord) return acc;

      const changedFields = Object.keys(newRecord).reduce((fields, key) => {
        if (newRecord[key] !== oldRecord[key]) {
          fields.push(key);
          switch (key) {
            case "consumption":     changedFieldsSet.add("FUEL_CONSUMPTION_DATA"); break;
            case "cslip":           changedFieldsSet.add("CSLIP_DATA"); break;
            case "ef_ttw_gfuel":
            case "ef_ttw_MJfuel":   changedFieldsSet.add("EF_TTW_DATA"); break;
            case "ef_wtt_gfuel":
            case "ef_wtt_MJfuel":   changedFieldsSet.add("EF_WTT_DATA"); break;
            case "lcv":             changedFieldsSet.add("LCV_DATA"); break;
            case "bunker_survey":   changedFieldsSet.add("BUNKER_SURVEY_DATA"); break;
            case "bunker_quantity": changedFieldsSet.add("BUNKER_QTY_DATA"); break;
            case "rob":             changedFieldsSet.add("ROB_DATA"); break;
            default:                changedFieldsSet.add(key); break;
          }
          changedFieldsSet.add(key);
        }
        return fields;
      }, []);

      if (changedFields.length > 0) {
        acc.push({
          fuel_consumption_id: newRecord.id,
          fuel_id: newRecord.fuel_id,
          fuel_metadata_id: newRecord.fuel_metadata_id,
          class_name: oldRecord.class_name,
          fields: specifiedFields.reduce((fields, key) => {
            switch (key) {
              case "name":          fields[key] = newRecord["fuel_name"]; break;
              case "ef_ttw_gfuel":  fields[key] = parseFloat(newRecord["fuel_ef_ttw_gfuel"]); break;
              case "ef_ttw_MJfuel": fields[key] = parseFloat(newRecord["fuel_ef_ttw_MJfuel"]); break;
              case "ef_wtt_gfuel":  fields[key] = parseFloat(newRecord["fuel_ef_wtt_gfuel"]); break;
              case "ef_wtt_MJfuel": fields[key] = parseFloat(newRecord["fuel_ef_wtt_MJfuel"]); break;
              case "lcv":           fields[key] = parseFloat(newRecord["fuel_lcv"]); break;
              case "cslip":         fields[key] = parseFloat(newRecord["fuel_cslip"]); break;
              case "rwdi":          fields[key] = parseFloat(newRecord["fuel_rwdi"]); break;
              default:              fields[key] = newRecord[key]; break;
            }
            return fields;
          }, {
            blended_id:
              blendedFuelsData?.general_info?.unique_id === selectedUniqueId
                ? blendedFuelsData?.blend_id
                : [],
          }),
          old_impacted_fields: changedFields.reduce(
            (fields, key) => { fields[key] = oldRecord[key]; return fields; },
            { fuel_name: oldRecord["fuel_name"], change_to: newRecord["fuel_name"] }
          ),
          new_impacted_fields: changedFields.reduce((fields, key) => {
            const value = newRecord[key];
            fields[key] = !isNaN(value) && value !== "" ? parseFloat(value) : value;
            return fields;
          }, { fuel_name: newRecord["fuel_name"], change_from: oldRecord["fuel_name"] }),
        });
      }
      return acc;
    }, []);

    return {
      data: changes,
      user_remark: userRemark,
      changed_fields: Array.from(changedFieldsSet),
    };
  };

  const handleApplyToThisRecord = async () => {
    if (isTableEdited && !userRemark) { setRemarkError(true); return; }

    const payload = createPayload({ oldData: fuelDataByUniqueId, newData: editingData });
    if (payload.data.length === 0) { handleClose(); return; }

    const hasBiofuelWithoutBlendedID = payload?.data?.some(
      (r) => r?.class_name === "Biofuel" && r?.fields?.blended_id?.length > 0 && !r?.fields?.name
    );
    if (hasBiofuelWithoutBlendedID) { message.error("Biofuel Name is mandatory"); return; }

    const payloadToSend = {
      payload,
      uniqueID: selectedUniqueId,
      imo_number: fuelDataByUniqueId[0].imo_number,
      casper_id: rawData[selectedUniqueId]?.[0]?.casper_id || "",
    };

    try {
      const response = await dispatch(patchFuelInfoByUniqueID(payloadToSend)).unwrap();
      if (response?.status === 200) {
        setIsTableEdited(false);
        dispatch(fetchFuelInfoByUniqueID(selectedUniqueId));
        if (payload.changed_fields.includes(cardDataTypeIndividual[selectedUniqueId]?.requestedData)) {
          dispatch(fetchRawData({
            ...globalFilters,
            requestedData: cardDataTypeIndividual[selectedUniqueId].requestedData,
            uniqueID: selectedUniqueId,
          }));
        }
      }
    } catch (error) {
      console.error("Error during API calls:", error);
    } finally {
      setUserRemark("");
      handleClose();
    }
  };

  const handleApplyToAllRecord = () => {
    if (!userRemark) { setRemarkError(true); return; }
    const payloadToSend = {
      referenced_unique_id: selectedUniqueId,
      referenced_data: fuelDataByUniqueId,
      apply_to: selectedCards.filter((item) => item !== selectedUniqueId),
      conflicting_data: [],
      user_remarks: userRemark,
      imo_number: fuelDataByUniqueId[0].imo_number,
      casper_id: rawData[selectedUniqueId]?.[0]?.casper_id || "",
    };
    dispatch(postFuelInfoApplyToAll(payloadToSend)).then((result) => {
      if (result.payload?.data?.available_options_to_resolve_conflict?.length > 0) {
        setConflictingDataModalVisible(true);
      } else {
        handleClose();
      }
    });
  };

  const handleInputChange = (e, record, key) => {
    const newValue =
      key === "fuel_name" || key === "class_name"
        ? e.target.value
        : !isNaN(e.target.value) || e.target.value === "."
        ? e.target.value
        : null;
    const newData = [...editingData];
    const index = newData.findIndex((item) => item.id === record.id);
    const item = newData[index];
    newData[index] = {
      ...item,
      [key]: newValue,
      modified: { ...item.modified, [key]: item[key] !== newValue },
    };
    setEditingData(newData);
    const hasChanged = !fuelDataByUniqueId.every((fuelItem, idx) =>
      Object.keys(fuelItem).every((k) => fuelItem[k] === newData[idx][k])
    );
    if (hasChanged) setIsTableEdited(true);
  };

  const handleSelectChange = (value, record, key) => {
    const newData = [...editingData];
    const index = newData.findIndex((item) => item.id === record.id);
    const item = newData[index];
    newData[index] = {
      ...item,
      [key]: value,
      modified: { ...item.modified, [key]: item[key] !== value },
    };
    setEditingData(newData);
    const hasChanged = !fuelDataByUniqueId.every((fuelItem, idx) =>
      Object.keys(fuelItem).every((k) => fuelItem[k] === newData[idx][k])
    );
    if (hasChanged) setIsTableEdited(true);
  };

  const handleClose = () => {
    setEditingData([]);
    setIsTableEdited(false);
    setUserRemark("");
    setRemarkError(false);
    setIsModalVisible(false);
  };

  const formatNumber = (value) => {
    if (typeof value === "number") {
      return value.toFixed(Math.min(6, (value.toString().split(".")[1] || "").length));
    }
    return value;
  };

  const columns = [
    {
      title: "Fuel name",
      dataIndex: "fuel_name",
      key: "fuel_name",
      width: 150,
      fixed: "left",
      render: (text, record) => (
        <Input
          className={record.modified?.fuel_name ? "fuel-class-custom-select custom-cell modified-cell" : "custom-cell"}
          value={text}
          onChange={(e) => handleInputChange(e, record, "fuel_name")}
          disabled={disabled}
        />
      ),
    },
    {
      title: "Fuel Class",
      dataIndex: "class_name",
      key: "class_name",
      width: 100,
      render: (text, record) => (
        <Select
          disabled={record?.class_name === "Biofuel" || disabled}
          className={record.modified?.class_name ? "fuel-class-custom-select modified-cell" : "fuel-class-custom-select"}
          value={text}
          onChange={(value) => handleSelectChange(value, record, "class_name")}
        >
          <Option value="Fossil">Fossil</Option>
          <Option value="RFNBO">RFNBO</Option>
          {!record?.fuel_name && !record?.fuel_id && <Option value="Biofuel">Biofuel</Option>}
        </Select>
      ),
    },
    {
      title: "Consumption(MT)",
      dataIndex: "consumption",
      key: "consumption",
      width: 150,
      render: (text, record) => (
        <Input
          className={record.modified?.consumption ? "modified-cell" : ""}
          value={formatNumber(text)}
          onChange={(e) => handleInputChange(e, record, "consumption")}
          disabled={disabled}
        />
      ),
    },
    {
      title: "LCV(MJ/MT)",
      dataIndex: "fuel_lcv",
      key: "fuel_lcv",
      width: 150,
      render: (text, record) => (
        <Input
          disabled={record?.class_name === "Biofuel" || disabled}
          className={record.modified?.fuel_lcv ? "modified-cell" : ""}
          value={formatNumber(text)}
          onChange={(e) => handleInputChange(e, record, "fuel_lcv")}
        />
      ),
    },
    {
      title: "Bunker Quantity(MT)",
      dataIndex: "bunker_quantity",
      key: "bunker_quantity",
      width: 150,
      render: (text, record) => (
        <Input
          disabled={record?.class_name === "Biofuel" || disabled}
          className={record.modified?.bunker_quantity ? "modified-cell" : ""}
          value={formatNumber(text)}
          onChange={(e) => handleInputChange(e, record, "bunker_quantity")}
        />
      ),
    },
    {
      title: "Bunker Survey(MT)",
      dataIndex: "bunker_survey",
      key: "bunker_survey",
      width: 150,
      render: (text, record) => (
        <Input
          disabled={record?.class_name === "Biofuel" || disabled}
          className={record.modified?.bunker_survey ? "modified-cell" : ""}
          value={formatNumber(text)}
          onChange={(e) => handleInputChange(e, record, "bunker_survey")}
        />
      ),
    },
    {
      title: "RoB (MT)",
      dataIndex: "rob",
      key: "rob",
      width: 150,
      render: (text, record) => (
        <Input
          className={record.modified?.rob ? "modified-cell" : ""}
          value={formatNumber(text)}
          onChange={(e) => handleInputChange(e, record, "rob")}
          disabled={disabled}
        />
      ),
    },
    {
      title: "Cslip",
      dataIndex: "fuel_cslip",
      key: "fuel_cslip",
      width: 150,
      render: (text, record) => (
        <Input
          className={record.modified?.fuel_cslip ? "modified-cell" : ""}
          value={formatNumber(text)}
          disabled={disabled}
          onChange={(e) => handleInputChange(e, record, "fuel_cslip")}
        />
      ),
    },
    {
      title: "EF-TTW (MJfuel)",
      dataIndex: "fuel_ef_ttw_MJfuel",
      key: "fuel_ef_ttw_MJfuel",
      width: 150,
      render: (text, record) => (
        <Input
          disabled={record?.class_name === "Biofuel" || disabled}
          className={record.modified?.fuel_ef_ttw_MJfuel ? "modified-cell" : ""}
          value={formatNumber(text)}
          onChange={(e) => handleInputChange(e, record, "fuel_ef_ttw_MJfuel")}
        />
      ),
    },
    {
      title: "EF-TTW (Gfuel)",
      dataIndex: "fuel_ef_ttw_gfuel",
      key: "fuel_ef_ttw_gfuel",
      width: 150,
      render: (text, record) => (
        <Input
          disabled={record?.class_name === "Biofuel" || disabled}
          className={record.modified?.fuel_ef_ttw_gfuel ? "modified-cell" : ""}
          value={formatNumber(text)}
          onChange={(e) => handleInputChange(e, record, "fuel_ef_ttw_gfuel")}
        />
      ),
    },
    {
      title: "EF-WTT (MJfuel)",
      dataIndex: "fuel_ef_wtt_MJfuel",
      key: "fuel_ef_wtt_MJfuel",
      width: 150,
      render: (text, record) => (
        <Input
          disabled={record?.class_name === "Biofuel" || disabled}
          className={record.modified?.fuel_ef_wtt_MJfuel ? "modified-cell" : ""}
          value={formatNumber(text)}
          onChange={(e) => handleInputChange(e, record, "fuel_ef_wtt_MJfuel")}
        />
      ),
    },
    {
      title: "EF-WTT (Gfuel)",
      dataIndex: "fuel_ef_wtt_gfuel",
      key: "fuel_ef_wtt_gfuel",
      width: 150,
      render: (text, record) => (
        <Input
          disabled={record?.class_name === "Biofuel" || disabled}
          className={record.modified?.fuel_ef_wtt_gfuel ? "modified-cell" : ""}
          value={formatNumber(text)}
          onChange={(e) => handleInputChange(e, record, "fuel_ef_wtt_gfuel")}
        />
      ),
    },
    {
      title: "RWDi",
      dataIndex: "fuel_rwdi",
      key: "fuel_rwdi",
      width: 50,
      render: (text, record) => (
        <Input
          className={record.modified?.fuel_rwdi ? "modified-cell" : ""}
          value={formatNumber(text)}
          onChange={(e) => handleInputChange(e, record, "fuel_rwdi")}
          disabled={disabled}
        />
      ),
    },
    {
      title: " Edit ",
      key: "settings",
      align: "center",
      width: 50,
      render: (_, record) =>
        record?.class_name === "Biofuel" ? (
          <SettingOutlined
            style={{ cursor: "pointer" }}
            onClick={() => {
              setActiveBiofuelId(record.id);
              setBioFuelModalVisible(true);
            }}
          />
        ) : (
          <Input value="" disabled />
        ),
    },
  ];

  return (
    <>
      <Modal
        title="Edit Details"
        visible={visible}
        onCancel={handleClose}
        footer={
          !disabled && [
            <div className="fuel-edit-modal-footer" key="footer">
              <div className="fuel-edit-modal-footer-remark">
                <Input
                  value={userRemark}
                  onChange={(e) => { setUserRemark(e.target.value); setRemarkError(false); }}
                  placeholder="User Remark *"
                  className="user-remark-input"
                  style={{ width: "70%", marginRight: "10px" }}
                />
                {remarkError && (
                  <span style={{ color: "red", marginLeft: "10px" }}>Remark is required.</span>
                )}
              </div>
              <div className="fuel-edit-modal-footer-buttons">
                <Button key="back" onClick={handleClose} icon={<ArrowLeftOutlined />} className="cancel-button-stepper">
                  Close
                </Button>
                <Button key="apply-this" type="primary" onClick={handleApplyToThisRecord} className="stepper-next-button" disabled={!isTableEdited}>
                  Apply to this record
                </Button>
                <Button key="apply-all" type="primary" onClick={handleApplyToAllRecord} className="stepper-next-button" disabled={isTableEdited}>
                  Apply to all record
                </Button>
              </div>
            </div>,
          ]
        }
        className="fuel-edit-modal"
        style={{ top: 50 }}
      >
        <Table
          dataSource={editingData}
          columns={columns}
          rowKey="id"
          pagination={false}
          scroll={{ x: 2100 }}
          className="fuel-data-stepper-table"
          bordered
        />
        <ConflictingDataModal
          visible={isConflictingDataModalVisible}
          setIsModalVisible={setConflictingDataModalVisible}
          selectedUniqueId={selectedUniqueId}
          onClose={handleClose}
        />
      </Modal>

      <BiofuelCompositionModal
        visible={bioFuelModalVisible}
        onClose={() => { setBioFuelModalVisible(false); setActiveBiofuelId(null); }}
        onSubmit={({ formData }) => {
          const newData = editingData.map((item) =>
            item.id === activeBiofuelId
              ? { ...item, fuel_name: formData.biofuelName, modified: { ...item.modified, fuel_name: true } }
              : item
          );
          setEditingData(newData);
          setIsTableEdited(true);
          setBioFuelModalVisible(false);
          setActiveBiofuelId(null);
        }}
        selectedYear={null}
      />
    </>
  );
};

export default FuelEditModal;
